import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { COL_PRODUCTION_REQUEST_LINES, COL_SCHEDULE_SLOTS } from '../../config/collections'
import { PERMISSION_KEYS } from '../../config/permissions'
import { pb } from '../../pocketbase'
import { useAuth } from '../../context/AuthContext'
import { ExportCsvButton } from '../../components/ExportCsvButton'
import type { CsvColumn } from '../../lib/exportCsv'
import { checkOverdueAndNotify } from '../../lib/overdueNotifications'
import { pocketbaseErrorMessage } from '../../lib/pocketbaseErrorMessage'
import {
  formatDateRu,
  formatQty,
  isLineOverdue,
} from '../../lib/scheduleCalculations'
import {
  REQUEST_LINE_STATUS_LABELS,
  remainingToSchedule,
  scheduledQuantitySum,
} from '../../lib/requestLineStatus'
import { pauseRequestLine, stopRequestLine } from '../../lib/scheduleOperations'
import type { ProductionRequestLineRecord, ScheduleSlotRecord } from '../../types/records'
import { Alert } from '../../components/ui'
import { ScheduleSlotFormModal } from './ScheduleSlotFormModal'
import {
  btn,
  cn,
  loadingClass,
  tableCellClass,
  tableClass,
  tableHeadClass,
  tableLastRowClass,
  tableRowHoverClass,
  tableWrapClass,
  toolbarClass,
} from '../../lib/ui'

function priorityBadge(priority: number, urgent?: boolean) {
  if (urgent) {
    return (
      <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800 dark:bg-red-950 dark:text-red-200">
        Срочно
      </span>
    )
  }
  if (priority >= 4) {
    return (
      <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800 dark:bg-orange-950 dark:text-orange-200">
        P{priority}
      </span>
    )
  }
  return <span className="text-su-text">P{priority}</span>
}

export function PlanningQueuePage() {
  const { user, can } = useAuth()
  const canWrite = can(PERMISSION_KEYS.PRODUCTION_SCHEDULE_WRITE)

  const [lines, setLines] = useState<ProductionRequestLineRecord[]>([])
  const [slotsByLine, setSlotsByLine] = useState<Map<string, ScheduleSlotRecord[]>>(new Map())
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [actionMsg, setActionMsg] = useState('')
  const [showOverdueOnly, setShowOverdueOnly] = useState(false)
  const [busyLineId, setBusyLineId] = useState<string | null>(null)

  const [modalOpen, setModalOpen] = useState(false)
  const [modalSlotId, setModalSlotId] = useState<string | null>(null)
  const [modalLineId, setModalLineId] = useState<string | undefined>()

  const load = useCallback(async () => {
    setErr('')
    setLoading(true)
    try {
      const [list, slotList] = await Promise.all([
        pb.collection(COL_PRODUCTION_REQUEST_LINES).getFullList<ProductionRequestLineRecord>({
          filter: 'status != "completed" && status != "shipped" && status != "cancelled"',
          expand: 'product,request',
          sort: '-priority,-request.clientUrgent,plannedProduction,request.receivedAt',
        }),
        pb.collection(COL_SCHEDULE_SLOTS).getFullList<ScheduleSlotRecord>({
          filter: 'status != "cancelled"',
          sort: 'plannedStart',
        }),
      ])
      const byLine = new Map<string, ScheduleSlotRecord[]>()
      for (const slot of slotList) {
        const lineId = typeof slot.requestLine === 'string' ? slot.requestLine : ''
        if (!lineId) continue
        const arr = byLine.get(lineId) ?? []
        arr.push(slot)
        byLine.set(lineId, arr)
      }
      setLines(list)
      setSlotsByLine(byLine)
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка загрузки очереди'))
      setLines([])
      setSlotsByLine(new Map())
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (!user?.id || loading) return
    void checkOverdueAndNotify(user.id)
  }, [user?.id, loading, lines])

  const queueCsvColumns: CsvColumn<ProductionRequestLineRecord>[] = useMemo(
    () => [
      { header: 'Приоритет', value: (ln) => ln.priority ?? 3 },
      { header: 'Срочно', value: (ln) => (ln.expand?.request?.clientUrgent ? 'да' : '') },
      { header: 'Продукция', value: (ln) => ln.expand?.product?.name ?? '' },
      { header: 'Кол-во', value: (ln) => ln.quantity },
      {
        header: 'Запланировано',
        value: (ln) => scheduledQuantitySum(slotsByLine.get(ln.id) ?? []),
      },
      {
        header: 'Остаток',
        value: (ln) => remainingToSchedule(ln, slotsByLine.get(ln.id) ?? []),
      },
      { header: 'Клиент', value: (ln) => ln.expand?.request?.clientName ?? '' },
      { header: 'План произв.', value: (ln) => formatDateRu(ln.plannedProduction) },
      { header: 'Статус', value: (ln) => REQUEST_LINE_STATUS_LABELS[ln.status] ?? ln.status },
    ],
    [slotsByLine],
  )

  const sortedLines = useMemo(() => {
    const copy = [...lines]
    copy.sort((a, b) => {
      const pa = a.priority ?? 3
      const pb_ = b.priority ?? 3
      if (pb_ !== pa) return pb_ - pa
      const ua = a.expand?.request?.clientUrgent ? 1 : 0
      const ub = b.expand?.request?.clientUrgent ? 1 : 0
      if (ub !== ua) return ub - ua
      const da = a.plannedProduction ? new Date(a.plannedProduction).getTime() : Infinity
      const db = b.plannedProduction ? new Date(b.plannedProduction).getTime() : Infinity
      if (da !== db) return da - db
      const ra = a.expand?.request?.receivedAt ? new Date(a.expand.request.receivedAt).getTime() : 0
      const rb = b.expand?.request?.receivedAt ? new Date(b.expand.request.receivedAt).getTime() : 0
      return ra - rb
    })
    return copy
  }, [lines])

  const displayed = useMemo(() => {
    if (!showOverdueOnly) return sortedLines
    return sortedLines.filter((ln) => isLineOverdue(ln))
  }, [sortedLines, showOverdueOnly])

  const overdueCount = useMemo(
    () => sortedLines.filter((ln) => isLineOverdue(ln)).length,
    [sortedLines],
  )

  function openSchedule(lineId: string) {
    setModalSlotId(null)
    setModalLineId(lineId)
    setModalOpen(true)
  }

  function openTransfer(lineId: string) {
    const slots = (slotsByLine.get(lineId) ?? []).filter(
      (s) => s.status === 'planned' || s.status === 'in_progress',
    )
    if (slots.length === 0) return
    setModalSlotId(slots[0].id)
    setModalLineId(undefined)
    setModalOpen(true)
  }

  async function handleStop(lineId: string) {
    if (!window.confirm('Остановить позицию? Все активные слоты будут отменены.')) return
    setBusyLineId(lineId)
    setErr('')
    try {
      const count = await stopRequestLine(lineId, { createdBy: user?.id })
      setActionMsg(`Позиция остановлена. Отменено слотов: ${count}.`)
      await load()
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка остановки'))
    } finally {
      setBusyLineId(null)
    }
  }

  async function handlePause(lineId: string) {
    if (
      !window.confirm(
        'Приостановить позицию? Будущие слоты (planned) отменятся, строка вернётся в ожидание.',
      )
    ) {
      return
    }
    setBusyLineId(lineId)
    setErr('')
    try {
      const count = await pauseRequestLine(lineId, { createdBy: user?.id })
      setActionMsg(`Позиция приостановлена. Отменено слотов: ${count}.`)
      await load()
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка приостановки'))
    } finally {
      setBusyLineId(null)
    }
  }

  if (loading && lines.length === 0) {
    return <div className={loadingClass}>Загрузка очереди…</div>
  }

  return (
    <div>
      <p className="mb-4 text-sm text-su-text">
        Очередь на планирование: приоритет, срочность клиента, дата производства. Просроченные строки выделены красным.
      </p>
      {err && <Alert kind="err">{err}</Alert>}
      {actionMsg && (
        <p className="mb-3 rounded-su border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-200">
          {actionMsg}
        </p>
      )}

      <div className={toolbarClass}>
        <button type="button" className={btn()} onClick={() => void load()} disabled={loading}>
          Обновить
        </button>
        <label className="flex items-center gap-2 text-sm text-su-text">
          <input
            type="checkbox"
            checked={showOverdueOnly}
            onChange={(e) => setShowOverdueOnly(e.target.checked)}
          />
          Только просроченные ({overdueCount})
        </label>
        {displayed.length > 0 && (
          <ExportCsvButton filename="planning-queue" rows={displayed} columns={queueCsvColumns} />
        )}
        {canWrite && (
          <Link to="/planning/schedule" className={btn('primary')}>
            Открыть график
          </Link>
        )}
      </div>

      <div className={cn(tableWrapClass, tableLastRowClass)}>
        <table className={tableClass}>
          <thead>
            <tr>
              <th className={tableHeadClass}>Приоритет</th>
              <th className={tableHeadClass}>Продукция</th>
              <th className={tableHeadClass}>Кол-во</th>
              <th className={tableHeadClass}>Запланировано</th>
              <th className={tableHeadClass}>Остаток</th>
              <th className={tableHeadClass}>Клиент</th>
              <th className={tableHeadClass}>План произв.</th>
              <th className={tableHeadClass}>Статус</th>
              {canWrite && <th className={tableHeadClass}>Действия</th>}
            </tr>
          </thead>
          <tbody>
            {displayed.length === 0 && (
              <tr className={tableRowHoverClass}>
                <td className={tableCellClass} colSpan={canWrite ? 9 : 8}>
                  <em>{showOverdueOnly ? 'Нет просроченных строк' : 'Очередь пуста'}</em>
                </td>
              </tr>
            )}
            {displayed.map((ln) => {
              const overdue = isLineOverdue(ln)
              const urgent = ln.expand?.request?.clientUrgent
              const lineSlots = slotsByLine.get(ln.id) ?? []
              const scheduled = scheduledQuantitySum(lineSlots)
              const remaining = remainingToSchedule(ln, lineSlots)
              const activeSlots = lineSlots.filter(
                (s) => s.status === 'planned' || s.status === 'in_progress',
              )
              const busy = busyLineId === ln.id
              const canAct =
                ln.status !== 'cancelled' && ln.status !== 'completed' && ln.status !== 'shipped'

              return (
                <tr
                  key={ln.id}
                  className={cn(tableRowHoverClass, overdue && 'bg-red-50/80 dark:bg-red-950/25')}
                >
                  <td className={tableCellClass}>{priorityBadge(ln.priority ?? 3, urgent)}</td>
                  <td className={cn(tableCellClass, overdue && 'font-semibold text-red-800 dark:text-red-300')}>
                    {ln.expand?.product?.name ?? ln.product}
                    {overdue && (
                      <span className="ml-2 rounded bg-red-600 px-1.5 py-0.5 text-[0.65rem] font-bold text-white">
                        Просрочено
                      </span>
                    )}
                  </td>
                  <td className={tableCellClass}>{formatQty(ln.quantity)}</td>
                  <td className={tableCellClass}>{formatQty(scheduled)}</td>
                  <td className={tableCellClass}>{formatQty(remaining)}</td>
                  <td className={tableCellClass}>{ln.expand?.request?.clientName ?? '—'}</td>
                  <td className={cn(tableCellClass, overdue && 'font-medium text-red-700 dark:text-red-300')}>
                    {formatDateRu(ln.plannedProduction)}
                  </td>
                  <td className={tableCellClass}>{REQUEST_LINE_STATUS_LABELS[ln.status] ?? ln.status}</td>
                  {canWrite && (
                    <td className={tableCellClass}>
                      {canAct && (
                        <div className="flex flex-wrap gap-1">
                          <button
                            type="button"
                            className={btn('ghost', 'sm')}
                            disabled={busy}
                            onClick={() => openSchedule(ln.id)}
                          >
                            Запланировать
                          </button>
                          {activeSlots.length > 0 && (
                            <button
                              type="button"
                              className={btn('ghost', 'sm')}
                              disabled={busy}
                              onClick={() => openTransfer(ln.id)}
                            >
                              Перенести
                            </button>
                          )}
                          <button
                            type="button"
                            className={btn('ghost', 'sm')}
                            disabled={busy}
                            onClick={() => void handlePause(ln.id)}
                          >
                            Приостановить
                          </button>
                          <button
                            type="button"
                            className={btn('ghost', 'sm')}
                            disabled={busy}
                            onClick={() => void handleStop(ln.id)}
                          >
                            Остановить
                          </button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <ScheduleSlotFormModal
          slotId={modalSlotId}
          initialRequestLineId={modalLineId}
          onClose={() => {
            setModalOpen(false)
            setModalSlotId(null)
            setModalLineId(undefined)
          }}
          onSaved={() => void load()}
        />
      )}
    </div>
  )
}
